import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchAdminPendingTransactions,
  adminApproveTransaction,
  adminRejectTransaction,
} from '../../util/api';

// ================ Thunks ================ //

export const fetchPendingThunk = createAsyncThunk(
  'AdminApprovalsPage/fetchPending',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchAdminPendingTransactions();
      return response.data;
    } catch (e) {
      return rejectWithValue(e.message || 'Failed to fetch pending transactions.');
    }
  }
);

export const approveThunk = createAsyncThunk(
  'AdminApprovalsPage/approve',
  async (transactionId, { rejectWithValue }) => {
    try {
      await adminApproveTransaction(transactionId);
      return transactionId;
    } catch (e) {
      return rejectWithValue(e.message || 'Failed to approve transaction.');
    }
  }
);

export const rejectThunk = createAsyncThunk(
  'AdminApprovalsPage/reject',
  async (transactionId, { rejectWithValue }) => {
    try {
      await adminRejectTransaction(transactionId);
      return transactionId;
    } catch (e) {
      return rejectWithValue(e.message || 'Failed to reject transaction.');
    }
  }
);

// ================ Slice ================ //

const adminApprovalsSlice = createSlice({
  name: 'AdminApprovalsPage',
  initialState: {
    pendingTransactions: [],
    fetchInProgress: false,
    fetchError: null,
    actionInProgress: null, // transactionId being acted on
    actionError: null,
  },
  reducers: {
    clearActionError: state => {
      state.actionError = null;
    },
  },
  extraReducers: builder => {
    builder
      // Fetch pending
      .addCase(fetchPendingThunk.pending, state => {
        state.fetchInProgress = true;
        state.fetchError = null;
      })
      .addCase(fetchPendingThunk.fulfilled, (state, action) => {
        state.fetchInProgress = false;
        state.pendingTransactions = action.payload;
      })
      .addCase(fetchPendingThunk.rejected, (state, action) => {
        state.fetchInProgress = false;
        state.fetchError = action.payload;
      })
      // Approve
      .addCase(approveThunk.pending, (state, action) => {
        state.actionInProgress = action.meta.arg;
        state.actionError = null;
      })
      .addCase(approveThunk.fulfilled, (state, action) => {
        state.actionInProgress = null;
        state.pendingTransactions = state.pendingTransactions.filter(
          tx => tx.id !== action.payload
        );
      })
      .addCase(approveThunk.rejected, (state, action) => {
        state.actionInProgress = null;
        state.actionError = action.payload;
      })
      // Reject
      .addCase(rejectThunk.pending, (state, action) => {
        state.actionInProgress = action.meta.arg;
        state.actionError = null;
      })
      .addCase(rejectThunk.fulfilled, (state, action) => {
        state.actionInProgress = null;
        state.pendingTransactions = state.pendingTransactions.filter(
          tx => tx.id !== action.payload
        );
      })
      .addCase(rejectThunk.rejected, (state, action) => {
        state.actionInProgress = null;
        state.actionError = action.payload;
      });
  },
});

export const { clearActionError } = adminApprovalsSlice.actions;
export default adminApprovalsSlice.reducer;

// ================ loadData ================ //

export const loadData = () => dispatch => {
  return dispatch(fetchPendingThunk());
};
